import {
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  Lock,
  Shield,
} from "lucide-react";
import React, { memo, useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import { authService } from "../services/api";

// Composant InputField mémorisé pour éviter les re-renders
const InputField = memo(
  ({
    name,
    label,
    type,
    placeholder,
    required = false,
    value,
    onChange,
    error,
    showCurrentPassword,
    setShowCurrentPassword,
    showNewPassword,
    setShowNewPassword,
    showConfirmPassword,
    setShowConfirmPassword,
  }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Lock className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`block w-full pl-10 pr-10 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
            error
              ? "border-red-300 bg-red-50"
              : "border-gray-300 hover:border-gray-400"
          }`}
        />
        <button
          type="button"
          onClick={() => {
            if (name === "current_password")
              setShowCurrentPassword(!showCurrentPassword);
            if (name === "new_password") setShowNewPassword(!showNewPassword);
            if (name === "confirm_password")
              setShowConfirmPassword(!showConfirmPassword);
          }}
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
        >
          {(name === "current_password" && showCurrentPassword) ||
          (name === "new_password" && showNewPassword) ||
          (name === "confirm_password" && showConfirmPassword) ? (
            <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          ) : (
            <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          )}
        </button>
      </div>
      {error && (
        <div className="flex items-center space-x-1 text-red-600 text-sm">
          <AlertCircle className="h-4 w-4" />
          <span>
            {typeof error === "string" ? error : "Erreur de validation"}
          </span>
        </div>
      )}
    </div>
  )
);

const ChangePassword = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.current_password) {
      newErrors.current_password = "Le mot de passe actuel est requis";
    }

    if (!formData.new_password) {
      newErrors.new_password = "Le nouveau mot de passe est requis";
    } else if (formData.new_password.length < 6) {
      newErrors.new_password =
        "Le mot de passe doit contenir au moins 6 caractères";
    }

    if (!formData.confirm_password) {
      newErrors.confirm_password =
        "La confirmation du mot de passe est requise";
    } else if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = "Les mots de passe ne correspondent pas";
    }

    if (formData.current_password === formData.new_password) {
      newErrors.new_password =
        "Le nouveau mot de passe doit être différent de l'actuel";
    }

    // S'assurer que toutes les erreurs sont des chaînes
    const safeErrors = {};
    Object.keys(newErrors).forEach((key) => {
      const error = newErrors[key];
      safeErrors[key] =
        typeof error === "string" ? error : "Erreur de validation";
    });

    setErrors(safeErrors);
    return Object.keys(safeErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Veuillez corriger les erreurs dans le formulaire");
      return;
    }

    setLoading(true);
    try {
      await authService.changePassword(
        formData.current_password,
        formData.new_password
      );

      toast.success("Mot de passe changé avec succès !");

      // Rediriger vers le dashboard après le changement
      setTimeout(() => {
        if (user?.role === "parent") {
          window.location.href = "/app/dashboard";
        } else if (user?.role === "admin" || user?.role === "superadmin") {
          window.location.href = "/app/admin";
        } else {
          // Fallback vers le dashboard parent
          window.location.href = "/app/dashboard";
        }
      }, 1000);
    } catch (error) {
      console.error("Password change error:", error);

      // Gérer les erreurs de validation
      let errorMessage = "Erreur lors du changement de mot de passe";

      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;

        // Si c'est une chaîne, l'utiliser directement
        if (typeof detail === "string") {
          errorMessage = detail;
        }
        // Si c'est un objet d'erreur de validation, extraire le message
        else if (typeof detail === "object" && detail.msg) {
          errorMessage = detail.msg;
        }
        // Si c'est un tableau d'erreurs, prendre la première
        else if (Array.isArray(detail) && detail.length > 0) {
          const firstError = detail[0];
          errorMessage =
            typeof firstError === "string"
              ? firstError
              : firstError.msg || errorMessage;
        }
      }

      if (errorMessage === "Mot de passe actuel incorrect") {
        setErrors({ current_password: "Mot de passe actuel incorrect" });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Clear error when user starts typing
      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    },
    [errors]
  );

  const toggleShowCurrentPassword = useCallback(() => {
    setShowCurrentPassword((prev) => !prev);
  }, []);

  const toggleShowNewPassword = useCallback(() => {
    setShowNewPassword((prev) => !prev);
  }, []);

  const toggleShowConfirmPassword = useCallback(() => {
    setShowConfirmPassword((prev) => !prev);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white">
              <Shield className="h-8 w-8" />
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Changement de mot de passe obligatoire
          </h2>
          <p className="text-gray-600">
            Pour des raisons de sécurité, vous devez changer votre mot de passe
            par défaut
          </p>
        </div>

        {/* Alert */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-orange-600 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-orange-800">
                Sécurité requise
              </h3>
              <p className="text-sm text-orange-700 mt-1">
                Vous ne pourrez pas accéder à votre compte tant que vous n'aurez
                pas changé votre mot de passe.
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            <InputField
              name="current_password"
              label="Mot de passe actuel"
              type={showCurrentPassword ? "text" : "password"}
              placeholder="Votre mot de passe actuel"
              required
              value={formData.current_password}
              onChange={handleChange}
              error={errors.current_password}
              showCurrentPassword={showCurrentPassword}
              setShowCurrentPassword={toggleShowCurrentPassword}
            />

            <InputField
              name="new_password"
              label="Nouveau mot de passe"
              type={showNewPassword ? "text" : "password"}
              placeholder="Votre nouveau mot de passe"
              required
              value={formData.new_password}
              onChange={handleChange}
              error={errors.new_password}
              showNewPassword={showNewPassword}
              setShowNewPassword={toggleShowNewPassword}
            />

            <InputField
              name="confirm_password"
              label="Confirmer le nouveau mot de passe"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirmez votre nouveau mot de passe"
              required
              value={formData.confirm_password}
              onChange={handleChange}
              error={errors.confirm_password}
              showConfirmPassword={showConfirmPassword}
              setShowConfirmPassword={toggleShowConfirmPassword}
            />

            {/* Password Requirements */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">
                Exigences du mot de passe :
              </h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Au moins 6 caractères
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Différent du mot de passe actuel
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Facile à retenir mais sécurisé
                </li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5 transition-all duration-200"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Changement en cours...
                </>
              ) : (
                <>
                  <Shield className="h-4 w-4 mr-2" />
                  Changer mon mot de passe
                </>
              )}
            </button>
          </form>
        </div>

        {/* Info */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white">
          <div className="flex items-center mb-4">
            <Shield className="h-5 w-5 mr-2" />
            <h3 className="text-lg font-semibold">Pourquoi ce changement ?</h3>
          </div>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center">
              <CheckCircle className="h-4 w-4 mr-2 text-green-300" />
              Sécuriser votre compte personnel
            </li>
            <li className="flex items-center">
              <CheckCircle className="h-4 w-4 mr-2 text-green-300" />
              Protéger vos données et celles de votre enfant
            </li>
            <li className="flex items-center">
              <CheckCircle className="h-4 w-4 mr-2 text-green-300" />
              Respecter les bonnes pratiques de sécurité
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
